import { IColumn } from '@fluentui/react';
import { Fragment, useContext, useEffect, useState } from 'react';

import AppContext from 'App/AppContext';
import {
  IPCheckbox,
  IPDefaultButton,
  IPLabel,
  IPPrimaryButton,
  IPSeparator,
  IPShimmeredDetailsList,
  IPText,
} from 'baseComponents';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPPanel, IPPanelType } from 'baseComponents/Panel/IPPanel';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import { IPToggle } from 'baseComponents/Toggle/IPToggle';
import HrefLink from 'components/Links/HrefLink';
import TextInfo from 'components/Notification/Info';
import { globalKB_howToWorkWithPowerBI } from 'globalConstants';
import { onRenderDetailsHeaderGlobal } from 'globalFunctions';
import {
  globalLayerRightSideBarProps,
  globalStackStylesPanel,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  globalTextStylesError,
} from 'globalStyles';
import Dashboard, { DashboardTypes, Dashboard_Translation } from 'models/dashboard';
import Group from 'models/group';
import { GroupMember } from 'models/groupMembers';
import Language from 'models/language';
import { PowerBI } from 'models/setting';
import { useTranslation } from 'react-i18next';
import { apiGetGroupMembership } from 'services/Api/userService';
import { apiRequest } from 'services/Auth/authConfig';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import AppError from 'utils/appError';
import { isEmpty } from 'utils/string';
import { IPSelectionMode } from "baseComponents/Utils/Selection";

interface IAddDashboardPanelProps {
  currentDashboard: Dashboard | undefined;
  dashboards: Dashboard[];
  isOpen: boolean;
  onSave: (dashboard: Dashboard) => void;
  onClose: () => void;
}

const AddDashboardPanel = (props: IAddDashboardPanelProps) => {
  const { t } = useTranslation(['translation', 'dashboard']);
  const appContext = useContext(AppContext);
  const [isPowerBI, setIsPowerBI] = useState<boolean>(false);
  const [nameError, setNameError] = useState<boolean>(false);
  const [dashboard, setDashboard] = useState<Dashboard | undefined>(undefined);
  const [groups, setGroups] = useState<Group[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [authError, setAuthError] = useState<boolean>(false);
  const [powerBIEnabled, setPowerBIEnabled] = useState<boolean>(false);

  useEffect(() => {
    if (props.isOpen) {
      loadData();
      setDashboard(getNewDashboard());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.currentDashboard, props.isOpen]);

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      if (props.currentDashboard?.dashboardType === DashboardTypes.team) {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        let groupsMembers: GroupMember[] = [];
        try {
          groupsMembers = await apiGetGroupMembership(accessToken, false);
        } catch (err) {
          const appErr = err as AppError;
          if (appErr.debug?.includes('Authorization_IdentityNotFound')) {
            setAuthError(true);
          }
        }

        const groups = groupsMembers.map((g) => appContext.globalDataCache.groups.get(g.groupId));
        groups.sort((a, b) => a.name.localeCompare(b.name));
        setGroups(groups);
      }

      //a Power BI report embeds with a Microsoft token, so it is off for a user of another identity provider
      const powerBIsetting = appContext.globalDataCache.settings.get(PowerBI) as boolean;
      setPowerBIEnabled(powerBIsetting && appContext.hasMicrosoftIdentity());
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getNewDashboard = (): Dashboard | undefined => {
    if (!props.currentDashboard) return;
    const newDashboard = new Dashboard(props.currentDashboard.dashboardType);
    newDashboard.sortOrder = props.currentDashboard?.sortOrder + 1;
    newDashboard.trans = [new Dashboard_Translation()];
    newDashboard.trans[0].name = t('dashboard:Panels.New.DefaultName');
    newDashboard.trans[0].lang = new Language();
    newDashboard.trans[0].lang.code = appContext.user.language.code;
    newDashboard.transIdx = 0;
    newDashboard.users = [];
    newDashboard.groups = [];

    return newDashboard;
  };

  const setDashboardName = (value: string | undefined) => {
    if (!dashboard) return;
    const dashboardClone = dashboard.clone();
    dashboardClone.name = value ?? '';
    setDashboard(dashboardClone);
  };

  const setDashboardUrl = (value: string | undefined) => {
    if (!dashboard) return;
    const dashboardClone = dashboard.clone();
    dashboardClone.url = value ?? '';
    setDashboard(dashboardClone);
  };

  const onSave = () => {
    if (!dashboard) return;
    props.onSave(dashboard);
  };

  const hasError = (): boolean => {
    if (props.currentDashboard?.dashboardType === DashboardTypes.team && dashboard?.groups?.length === 0) return true;
    if (isPowerBI && isEmpty(dashboard?.url)) return true;
    if (authError) return true;

    return nameError;
  };

  const onGetDashboardNameError = (value: string | undefined): string | undefined => {
    if (value) {
      if (
        value !== props.currentDashboard?.name &&
        props.dashboards.find((d) => d.name.toLocaleLowerCase() === value.toLowerCase())
      ) {
        return t('dashboard:Panels.New.ErrorNameExists');
      }

      setNameError(false);

      return undefined;
    } else {
      setNameError(true);

      return undefined;
    }
  };

  const columns: IColumn[] = [
    {
      key: 'checked',
      name: t('dashboard:Panels.New.List.Checked'),
      minWidth: 80,
      maxWidth: 80,
      isResizable: true,
      onRender: (item?: Group, index?: number, column?: IColumn) => {
        if (!item || !dashboard || !dashboard.groups) return;
        const checked = dashboard.groups.findIndex((d) => d.id === item.id) >= 0;

        return <IPCheckbox checked={checked} onChange={(ev, checked) => onSelectTeam(item, checked ?? false)} />;
      },
    },
    {
      isMultiline: true,
      key: 'name',
      name: t('dashboard:Panels.New.List.Group'),
      minWidth: 100,
      maxWidth: 300,
      isResizable: true,
      onRender: (item?: Group, index?: number, column?: IColumn) => {
        if (!item) return;

        return <IPPersona text={item.name} secondaryText={item.email} size={IPPersonaSize.size40} />;
      },
    },
  ];

  const onSelectTeam = (group: Group, checked: boolean) => {
    if (!dashboard) return;
    const newDashboard = dashboard.clone();
    if (checked) {
      newDashboard.groups?.push(group);
    } else {
      newDashboard.groups = newDashboard.groups?.filter((g) => g.id !== group.id);
    }
    setDashboard(newDashboard);
  };

  //
  // Render
  //

  const onRenderFooterContentConfiguredWidget = () => {
    return (
      <IPStack horizontal styles={globalStackStylesPanel} tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPPrimaryButton onClick={() => onSave()} disabled={hasError()}>
            {t('translation:General.Button.Save')}
          </IPPrimaryButton>
        </IPStack.Item>
        <IPStack.Item>
          <IPDefaultButton onClick={props.onClose}>{t('translation:General.Button.Cancel')}</IPDefaultButton>
        </IPStack.Item>
      </IPStack>
    );
  };

  return (
    <IPPanel
      headerText={t('dashboard:Panels.New.Title')}
      type={IPPanelType.medium}
      isBlocking={true}
      isHiddenOnDismiss={false}
      isOpen={props.isOpen}
      onDismiss={props.onClose}
      isFooterAtBottom={true}
      layerProps={globalLayerRightSideBarProps}
      onRenderFooterContent={onRenderFooterContentConfiguredWidget}
    >
      <IPStack verticalFill styles={globalStackStylesPanel} tokens={globalStackTokensGapMedium}>
        {authError && (
          <IPStack.Item>
            <IPText block styles={globalTextStylesError}>
              {t('dashboard:Panels.New.AuthError')}
            </IPText>
            <HrefLink href="/admin/settings/integration">{t('dashboard:Panels.New.AuthLink')}</HrefLink>
          </IPStack.Item>
        )}
        <IPStack.Item>
          <IPTextField
            label={t('dashboard:Panels.New.NameLabel')}
            autoFocus={!appContext.isMobileView}
            required
            value={dashboard?.name ?? ''}
            onChange={(ev, newValue) => setDashboardName(newValue)}
            onGetErrorMessage={onGetDashboardNameError}
            maxLength={128}
            placeholder={t('dashboard:Panels.New.NamePlaceholder')}
          />
          <IPSeparator />
        </IPStack.Item>
        {props.currentDashboard?.dashboardType === DashboardTypes.team && (
          <Fragment>
            <IPStack.Item>
              <IPText>{t('dashboard:Panels.New.TeamInfo')}</IPText>
            </IPStack.Item>
            <IPStack.Item>
              <IPShimmeredDetailsList
                shimmerLines={5}
                enableShimmer={isLoading}
                onRenderDetailsHeader={onRenderDetailsHeaderGlobal}
                compact
                layoutMode={IPDetailsListLayoutMode.justified}
                items={groups}
                columns={columns}
                selectionMode={IPSelectionMode.none}
                isHeaderVisible={true}
              />
            </IPStack.Item>
          </Fragment>
        )}
        {hasUserFeature(appContext, FeatureTypes.PowerBI) && (
          <Fragment>
            <IPStack.Item>
              <IPToggle
                checked={isPowerBI}
                disabled={!powerBIEnabled}
                label={
                  <TextInfo message={t('dashboard:PowerBI.Info')} learnMoreLink={globalKB_howToWorkWithPowerBI}>
                    <IPLabel>{t('dashboard:PowerBI.Label')}</IPLabel>
                  </TextInfo>
                }
                onChange={(ev, checked) => {
                  setIsPowerBI(checked ?? false);
                  if (!checked) {
                    setDashboardUrl(undefined);
                  }
                }}
              ></IPToggle>
            </IPStack.Item>
            <IPStack.Item>
              <TextInfo message={t('dashboard:PowerBI.UrlInfo')}>
                <IPLabel>{t('dashboard:PowerBI.UrlLabel')}</IPLabel>
              </TextInfo>
              <IPTextField
                autoFocus={!appContext.isMobileView}
                required={isPowerBI}
                disabled={!isPowerBI}
                value={dashboard?.url ?? ''}
                multiline
                style={{
                  resize: 'vertical',
                  maxHeight: 100,
                }}
                onChange={(ev, newValue) => setDashboardUrl(newValue)}
                maxLength={2000}
                placeholder={t('dashboard:PowerBI.UrlPlaceholder')}
              ></IPTextField>
            </IPStack.Item>
          </Fragment>
        )}
      </IPStack>
    </IPPanel>
  );
};

export default AddDashboardPanel;
